import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { PageHeader } from '../components/PageHeader';
import { EquipmentOptionLabel } from '../components/EquipmentCell';
import { ArrowLeft, AlertTriangle, RotateCcw, CheckCircle2, X, Clock, Truck, Search, Layers } from 'lucide-react';
import { formatDateTime } from '../lib/format';
import { useToast } from '../hooks/use-toast';

const REASON_LABELS = {
  unavailable: 'Unavailable',
  damaged: 'Damaged',
  not_arrived: 'Not yet arrived',
  other: 'Other',
};

const EquipmentQueues = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const focusId = params.get('focus');

  const [queues, setQueues] = useState([]);
  const [tab, setTab] = useState('queued');
  const [picker, setPicker] = useState(null); // queue entry being assigned
  const [alts, setAlts] = useState([]);
  const [loadingAlts, setLoadingAlts] = useState(false);
  const [picked, setPicked] = useState('');
  const [cancel, setCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const canAssign = hasRole(user, 'super_admin', 'admin', 'supervisor', 'approver');

  const load = useCallback(async () => {
    const r = await api.get('/equipment-queues');
    setQueues(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAssign = async (q) => {
    setPicker(q); setPicked(''); setLoadingAlts(true);
    try {
      const r = await api.get(`/equipment-queues/${q.id}/alternatives`);
      setAlts(r.data);
    } finally { setLoadingAlts(false); }
  };

  const submitAssign = async () => {
    if (!picked) return;
    try {
      await api.post(`/equipment-queues/${picker.id}/assign`, { vehicle_id: picked });
      toast({ title: 'Alternative assigned', description: 'Inspection sent back for processing.' });
      setPicker(null);
      // navigate back to inspections page
      setTimeout(() => navigate('/inspections'), 700);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const submitCancel = async () => {
    try {
      await api.post(`/equipment-queues/${cancel.id}/cancel`, { reason: cancelReason });
      toast({ title: 'Queue cancelled', description: 'Inspection restored to pending.' });
      setCancel(null); setCancelReason('');
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  // group queued by category for the "queued" tab view
  const queuedItems = queues.filter((q) => q.status === 'queued');
  const assignedItems = queues.filter((q) => q.status === 'assigned');
  const cancelledItems = queues.filter((q) => q.status === 'cancelled');

  const groupedByCategory = queuedItems.reduce((acc, q) => {
    const k = q.requested_category || 'Uncategorized';
    acc[k] = acc[k] || [];
    acc[k].push(q);
    return acc;
  }, {});

  const visible = tab === 'queued' ? queuedItems : tab === 'assigned' ? assignedItems : cancelledItems;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Equipment Queues"
        description="When the inspected equipment is unavailable, damaged or not yet arrived, the Queuing team finds a similar operational alternative for the same category. If none is available, the request waits in the category queue until one becomes operational."
        actions={
          <Button variant="outline" onClick={() => navigate('/inspections')}>
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Inspections
          </Button>
        }
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Clock} label="In Queue" value={queuedItems.length} accent="amber" />
        <Tile icon={Layers} label="Categories" value={Object.keys(groupedByCategory).length} accent="sky" />
        <Tile icon={CheckCircle2} label="Assigned" value={assignedItems.length} accent="emerald" />
        <Tile icon={X} label="Cancelled" value={cancelledItems.length} accent="slate" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          ['queued', 'In Queue', queuedItems.length],
          ['assigned', 'Assigned', assignedItems.length],
          ['cancelled', 'Cancelled', cancelledItems.length],
        ].map(([t, label, count]) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t ? 'border-[hsl(217,91%,24%)] text-[hsl(217,91%,24%)]' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>
            {label} <span className="ml-1.5 text-xs bg-slate-100 px-1.5 py-0.5 rounded-full">{count}</span>
          </button>
        ))}
      </div>

      {tab === 'queued' && Object.keys(groupedByCategory).length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-sm text-slate-500">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-300" />
            <div className="mt-3 font-semibold text-slate-700">No equipment in the queue</div>
            <div className="mt-1">Inspections proceed normally. When an item is sent here from the Inspections page, you'll see it grouped by category below.</div>
          </CardContent>
        </Card>
      ) : tab === 'queued' ? (
        <div className="space-y-4">
          {Object.entries(groupedByCategory).sort(([a],[b]) => a.localeCompare(b)).map(([cat, list]) => (
            <Card key={cat}>
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-[hsl(217,91%,24%)]" />
                    <h3 className="font-semibold text-slate-900">{cat}</h3>
                    <Badge variant="secondary" className="bg-blue-100 text-[hsl(217,91%,24%)]">{list.length} in queue</Badge>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-slate-600 border-b">
                      <tr>
                        <th className="p-3">Reference</th>
                        <th className="p-3">Original Equipment</th>
                        <th className="p-3">Reason</th>
                        <th className="p-3">Created</th>
                        <th className="p-3">By</th>
                        <th className="p-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {list.map((q) => (
                        <tr key={q.id} className={`table-row-hover ${focusId === q.id ? 'bg-amber-50/60' : ''}`}>
                          <td className="p-3 font-mono text-xs">{q.reference_number}</td>
                          <td className="p-3">
                            {q.original_vehicle ? (
                              <div className="space-y-0.5">
                                <div className="font-mono font-semibold text-sm">{q.original_vehicle.equipment_code}</div>
                                <div className="text-xs text-slate-500 truncate max-w-xs">{q.original_vehicle.description}</div>
                              </div>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="p-3"><Badge variant="secondary" className="bg-amber-100 text-amber-800"><AlertTriangle className="h-3 w-3 mr-1" />{REASON_LABELS[q.reason] || q.reason}</Badge>{q.notes && <div className="text-[11px] text-slate-500 mt-1 max-w-xs">{q.notes}</div>}</td>
                          <td className="p-3 text-xs">{formatDateTime(q.created_at)}</td>
                          <td className="p-3 text-xs font-mono">{q.created_by}</td>
                          <td className="p-3 text-right whitespace-nowrap">
                            {canAssign && (
                              <Button size="sm" onClick={() => openAssign(q)} className="bg-emerald-600 hover:bg-emerald-700"><Search className="h-3.5 w-3.5 mr-1" /> Find Alternative</Button>
                            )}
                            {canAssign && <Button size="sm" variant="ghost" onClick={() => { setCancel(q); setCancelReason(''); }}><X className="h-3.5 w-3.5 text-red-600" /></Button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            {visible.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">No entries.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b text-left text-slate-600">
                    <tr><th className="p-3">Reference</th><th className="p-3">Category</th><th className="p-3">Original Equipment</th><th className="p-3">Outcome</th><th className="p-3">When</th></tr>
                  </thead>
                  <tbody className="divide-y">
                    {visible.map((q) => (
                      <tr key={q.id} className="table-row-hover">
                        <td className="p-3 font-mono text-xs">{q.reference_number}</td>
                        <td className="p-3">{q.requested_category ? <Badge className="bg-blue-100 text-[hsl(217,91%,24%)]">{q.requested_category}</Badge> : '—'}</td>
                        <td className="p-3"><span className="font-mono">{q.original_vehicle?.equipment_code || '—'}</span></td>
                        <td className="p-3">
                          {q.status === 'assigned' ? (
                            <div className="space-y-0.5">
                              <Badge className="bg-emerald-100 text-emerald-800">Assigned</Badge>
                              <div className="text-xs"><span className="font-mono font-semibold">{q.assigned_vehicle?.equipment_code}</span> – {q.assigned_vehicle?.description}</div>
                            </div>
                          ) : (
                            <div className="space-y-0.5">
                              <Badge variant="secondary">Cancelled</Badge>
                              {q.cancel_reason && <div className="text-[11px] text-slate-500">{q.cancel_reason}</div>}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-xs">{formatDateTime(q.assigned_at || q.cancelled_at || q.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Assign dialog */}
      <Dialog open={!!picker} onOpenChange={(o) => !o && setPicker(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5" /> Find Alternative Equipment</DialogTitle>
            <DialogDescription>
              {picker?.requested_category ? <>Showing operational alternatives in category <span className="font-semibold text-[hsl(217,91%,24%)]">{picker.requested_category}</span></> : 'Showing operational alternatives.'}
            </DialogDescription>
          </DialogHeader>
          {loadingAlts ? (
            <div className="p-6 text-center text-sm text-slate-500">Loading alternatives…</div>
          ) : alts.length === 0 ? (
            <Alert className="bg-amber-50 border-amber-200 text-amber-900">
              <AlertDescription>
                <div className="font-semibold mb-1">No alternative equipment available right now</div>
                <div className="text-xs">The request stays in the <span className="font-semibold">{picker?.requested_category || 'category'}</span> queue. When a matching equipment becomes operational, return here and assign it.</div>
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <Alert className="bg-blue-50 border-blue-200 text-blue-900">
                <AlertDescription className="text-xs">
                  <span className="font-semibold">{alts.length}</span> alternative{alts.length !== 1 ? 's' : ''} available. Selecting one will assign it to the inspection and return the workflow back to <span className="font-semibold">Inspections</span> for processing.
                </AlertDescription>
              </Alert>
              <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
                {alts.map((v) => (
                  <label key={v.id} className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50 ${picked === v.id ? 'bg-blue-50/70 border-l-2 border-[hsl(217,91%,24%)]' : ''}`}>
                    <input type="radio" name="alt" className="mt-1" checked={picked === v.id} onChange={() => setPicked(v.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-semibold">{v.equipment_code}</span>
                        {v.category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-[hsl(217,91%,24%)] border border-blue-200">{v.category}</span>}
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">Operational</Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{v.description || [v.brand || v.make, v.model].filter(Boolean).join(' ')}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{v.area} · {v.project_location} · {v.company_assigned}</div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPicker(null)}>Close</Button>
            <Button onClick={submitAssign} disabled={!picked} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Assign & Return to Inspections</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancel} onOpenChange={(o) => !o && setCancel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Queue Entry</DialogTitle>
            <DialogDescription>The original inspection will be restored to pending status with its original equipment.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} placeholder="Reason (optional)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancel(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitCancel}>Confirm Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Tile = ({ icon: Icon, label, value, accent }) => {
  const palette = {
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  }[accent];
  return (
    <div className={`rounded-lg border bg-white p-3 flex items-center gap-3 border-slate-200`}>
      <div className={`h-9 w-9 rounded-md grid place-items-center border ${palette}`}><Icon className="h-4 w-4" /></div>
      <div>
        <div className="text-[11px] text-slate-500">{label}</div>
        <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
      </div>
    </div>
  );
};

export default EquipmentQueues;
